/**
 * Forms the console can fill: their versions, and where a version came from.
 *
 * A template with no stored structure is shown rather than hidden. That state is real —
 * someone named a form and extraction has not finished, or failed — and hiding it would
 * leave a person wondering where their template went.
 */
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { get, post } from "../lib/api";
import { usePermission } from "../hooks/useSession";
import type { TemplateSummary } from "../lib/types";
import { Shell } from "../components/Shell";
import { DataTable } from "../components/DataTable";
import { Button, Chip, Empty, Failed, Panel } from "../components/ui";
import { ago } from "../lib/format";

export function Templates() {
  const client = useQueryClient();
  const navigate = useNavigate();
  const can = usePermission();
  const canWrite = can("documents:write");
  const [name, setName] = useState("");

  const listing = useQuery({
    queryKey: ["templates"],
    queryFn: () => get<{ templates: TemplateSummary[] }>("/v1/templates"),
  });
  const create = useMutation({
    mutationFn: () => post<TemplateSummary>("/v1/templates", { name }),
    onSuccess: (created) => {
      setName("");
      void client.invalidateQueries({ queryKey: ["templates"] });
      navigate(`/templates/${created.id}`);
    },
  });

  const rows = listing.data?.templates ?? [];

  return (
    <Shell fill crumb={<span className="text-text">templates</span>}>
      <Panel
        fill
        title="templates"
        actions={
          canWrite && (
            <form
              onSubmit={(event) => {
                event.preventDefault();
                if (name.trim() !== "") create.mutate();
              }}
              className="flex gap-2"
            >
              <input
                aria-label="new template name"
                value={name}
                onChange={(event) => setName(event.target.value)}
                placeholder="new form"
                className="border border-hair bg-transparent px-2 py-1 font-mono text-[11.5px]"
              />
              <Button type="submit" disabled={create.isPending}>
                create
              </Button>
            </form>
          )
        }
      >
        {listing.isError ? (
          <Failed error={listing.error} what="templates" />
        ) : !listing.isPending && rows.length === 0 ? (
          <Empty
            title="no forms yet"
            hint="A form is a structure a document set gets answered against. Create one, then extract its structure from a scan of the blank form."
          />
        ) : (
          <DataTable
            fill
            table="templates"
            loading={listing.isPending}
            rows={rows}
            rowKey={(row) => row.id}
            onRowClick={(row) => navigate(`/templates/${row.id}`)}
            columns={[
              { key: "name", header: "name", width: 300, render: (row) => row.name },
              {
                key: "version",
                header: "structure",
                width: 150,
                render: (row) =>
                  row.latest_version === null ? (
                    <Chip tone="warn">no structure</Chip>
                  ) : (
                    <span className="tnum font-mono text-[11.5px]">v{row.latest_version}</span>
                  ),
              },
              {
                key: "created",
                header: "created",
                width: 130,
                cellClass: "text-dim",
                render: (row) => (row.created_at ? ago(row.created_at) : "—"),
              },
              {
                key: "state",
                header: "",
                width: 110,
                render: (row) => (row.archived_at ? <Chip tone="mut">archived</Chip> : null),
              },
            ]}
          />
        )}
      </Panel>
    </Shell>
  );
}
